import { KeyGetter, ItemValue, Key } from './types'
import { getKey, toArray, isEmpty, isArray, isUndefined } from './utils'
import { Item, ItemsMap } from './items'

export type Selected = Record<Key, boolean>
export type ValuesList<T = ItemValue> = (T | null)[]
export type Value<T = ItemValue> = ValuesList<T> | T | null

/**
 * Generates selecteds map.
 *
 * @category Functions
 * @export
 * @param values - Current value after `makeValues`.
 * @returns Map of key-(selected boolean) for faster selection check.
 */
export function makeSelected<T = ItemValue>(values: ItemsMap<T>): Selected {
  return values.items.reduce((acc: Selected, value: Item<T>) => {
    acc[value.key] = true

    return acc
  }, {})
}

export function clearValues<T = ItemValue>(keyGetter: KeyGetter<T>, values: ValuesList<T>): ValuesList<T> {
  const result = values.filter(
    (x: any) => !isEmpty(x) && !isEmpty(getKey(keyGetter, x))
  )

  return result.length === 0 ? [null] : result
}

/**
 * Prepares values for further use.
 * Autoupdates passed values from options(if values are just keys -
 * they also will be resolved).
 *
 * **Example:**
 * ```js
 * const key = x => x.id
 * const options = makeOptions(key, [
 *   { id: null, label: 'Not selected' },
 *   { id: 'other-option', label: 'Other option' },
 *   { id: 'third-option', label: 'Third option' },
 * ])
 * const value = makeValues(
 *   key, options, [
 *     null,
 *     options.items[1].value,
 *     null,
 *     options.items[2].value.id,
 *     null
 *   ]
 * )
 *
 * console.log(value.items)
 * // > [
 * //   {
 * //     key: 'other-option',
 * //     value: { id: 'other-option', label: 'Other option' },
 * //   },
 * //   {
 * //     key: 'third-option',
 * //     value: { id: 'third-option', label: 'Third option' },
 * //   },
 * // ]
 * ```
 *
 * @category General
 * @export
 * @param keyGetter - Option key resolving function.
 * @param options - Options ItemsMap.
 * @param value - Singular value or array of them.
 * @returns Values ItemsMap.
 */
export function makeValues<T = ItemValue>(
  keyGetter: KeyGetter<T>,
  options: ItemsMap<T>,
  value: Value<T>
): ItemsMap<T> {
  return new ItemsMap<T>(clearValues(keyGetter, toArray(value)).map((item: any) => {
    const key = getKey(keyGetter, item)
    const option = options.get(key)

    return option ? option : { key, value: item }
  }))
}

/**
 * Updates current value with a new item/items.
 *
 * **Example:**
 * ```js
 * const key = x => x.id
 * const options = makeOptions(key, [
 *   { id: null, label: 'Not selected' },
 *   { id: 'other-option', label: 'Other option' },
 *   { id: 'third-option', label: 'Third option' },
 * ])
 * const [, option1, option2] = options.items
 * const value = makeValues(key, options, option1.value)
 *
 * // Singular value:
 * let updated = updateValue(key, false, value, null)
 * // By passing null everything in the set will be erased to a `null` value.
 * // > [null]
 *
 * updated = updateValue(key, false, makeValues(key, options, updated), option1.value)
 * // > [{ id: 'other-option', label: 'Other option' }]
 *
 * updated = updateValue(key, false, makeValues(key, options, updated), option2.value)
 * // In a singular variant avery value will replace existing.
 * // > [{ id: 'third-option', label: 'Third option' }]
 *
 * updated = updateValue(key, false, makeValues(key, options, updated), option2.value)
 * // If passed already exists in current value - it will be removed.
 * // > [null]
 *
 * // Multiple value
 * updated = updateValue(key, true, makeValues(key, options, updated), option1.value)
 * // > [{ id: 'other-option', label: 'Other option' }]
 *
 * updated = updateValue(key, true, makeValues(key, options, updated), option2.value)
 * // > [
 * //   { id: 'other-option', label: 'Other option' },
 * //   { id: 'third-option', label: 'Third option' },
 * // ]
 *
 * updateValue(key, true, makeValues(key, options, updated), null)
 * // By passing null everything in the set will be erased to a `null` value.
 * // > [null]
 *
 * updated = updateValue(key, true, makeValues(key, options, updated), option2.value)
 * // If passed already exists in current value - it will be removed.
 * // > [{ id: 'other-option', label: 'Other option' }]
 *
 * updated = updateValue(key, true, makeValues(key, options, updated), option1.value)
 * // Value will always turn into `[null]` if there is no actual values left.
 * // > [null]
 * ```
 *
 * @category General
 * @export
 * @param keyGetter - Option key resolving function.
 * @param multiple - Defines whether there could be multiple values or not.
 * @param current - Current value from `makeValues`.
 * @param needle - Value item that should update current state.
 * @returns List of raw value items.
 */
export function updateValue<T = ItemValue>(
  keyGetter: KeyGetter<T>,
  multiple: boolean,
  current: ItemsMap<T>,
  needle: Value<T>,
): ValuesList<T> {
  let value = needle
  const key = getKey(keyGetter, needle as T | null)

  if (isEmpty(key) && !isArray(needle)) return [null]

  if (!isArray(needle)) {
    if (!multiple || !needle) {
      if (current.items[0]?.key === key) {
        value = []
      } else {
        value = [needle as T]
      }
    } else {
      value = current.raw()
      const index = current.indexMap[key]
      if (isUndefined(index)) {
        value.push(needle)
      } else {
        value.splice(index, 1)
      }
    }
  }

  return clearValues(keyGetter, value as ValuesList<T>)
}

/**
 * Turns values list into value, that is fits external "input".
 *
 * @category Functions
 * @export
 * @param multiple - Defines whether there could be multiple values or not.
 * @param values - List of "external" values.
 * @returns Value of values list, depending on multiple parameter.
 */
export function makeExternalValue(multiple: boolean, values: ItemValue[]) {
  const unnuled = values.filter(value => value !== null)

  return multiple ? unnuled : unnuled[0]
}
